import React from 'react';
import { ArrowPathIcon } from '@heroicons/react/16/solid';
import FormButton from './FormButton';

type Props = {
  label: string,
  phase?: 'idle' | 'running' | 'finishing',
  progress?: number,
  disabled?: boolean,
  joined?: boolean,
  // `white` is the secondary look (a second run action next to a primary one). Default `primary`.
  state?: 'primary' | 'white',
  // `small` is the compact toolbar size (text-xs). Default `normal`.
  size?: 'normal' | 'small',
  // Short, quiet text after the label, for example the age of the last run ("· 3 hours ago").
  hint?: string,
  fullWidth?: boolean,
  onClick?: React.MouseEventHandler<HTMLButtonElement>,
};

// The calendar and other run actions share this control. The caller owns the process.
const RunButton: React.FunctionComponent<Props> = ({
  label, phase = 'idle', progress, disabled = false, joined = false, state = 'primary', size = 'normal', hint, fullWidth = false, onClick,
}) => {
  const busy = phase !== 'idle';
  const percent = progress !== undefined && Number.isFinite(progress) ? Math.min(100, Math.max(0, progress)) : undefined;
  const small = size === 'small';
  const hintColour = state === 'primary' ? 'text-blue-100' : 'text-slate-400';
  return <span className={`relative ${fullWidth ? 'flex w-full' : 'inline-flex'}`} aria-busy={busy}>
    <FormButton state={state} size={size} disabled={disabled || busy} onClick={onClick}
      extraClassName={`relative ${small ? '' : 'py-1.5'} ${joined ? 'rounded-r-none' : ''} ${fullWidth ? 'w-full' : ''}`}
      icon={<ArrowPathIcon aria-hidden="true" className={`${small ? 'h-3.5 w-3.5' : 'mt-0.5 h-4 w-4'} ${busy ? 'animate-spin' : ''}`} />}>
      {label}
      {hint !== undefined && hint !== '' && <span className={`ml-1.5 whitespace-nowrap font-normal ${hintColour}`}>{hint}</span>}
    </FormButton>
    {busy && <span role="progressbar" aria-label={`${label} progress`} aria-valuemin={0} aria-valuemax={100}
      aria-valuenow={phase === 'running' ? percent : undefined}
      aria-valuetext={phase === 'finishing' ? 'Final steps' : undefined}
      className="pointer-events-none absolute inset-x-0 -bottom-px flex h-1 overflow-hidden rounded bg-gray-200">
      <span className={phase === 'finishing' || percent === undefined ? 'h-full w-full animate-progressbar origin-left bg-blue-600' : 'bg-gradient-to-r from-blue-200 to-blue-500 transition-all'}
        style={phase === 'running' && percent !== undefined ? { width: `${percent}%` } : undefined} />
    </span>}
  </span>;
};
export default RunButton;
